import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useLocation} from "react-router-dom";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {RequireReauth} from "@repo/user-settings";

import DeleteAccountModal from "./delete-account-modal.tsx";
import {strings} from "./strings.ts";
import {useFocusAnchorToId} from "./use-focus-anchor-with-id.ts";

type Props = {
    actorIdentifier: string | null | undefined;
    // If the user is a parent modifying their child's settings.
    // Please note that the child may be over 13.
    actorIsChild: boolean;
    isClasslinkUser: boolean;
    isModifyingChild: boolean;
    badgeCount: number;
    points: number;
    targetHasChildren: boolean;
    targetIsChild: boolean;
    targetNickname: string;
    targetId: string;
};

function DeleteAccount(props: Props): React.ReactElement {
    const id = "delete";
    useFocusAnchorToId(id);
    const [reauthSuccessful, setReauthSuccessful] = React.useState(false);
    const location = useLocation();

    function renderDeleteButton(): React.ReactElement {
        const {
            isModifyingChild,
            targetNickname,
            actorIdentifier,
            targetId,
            targetIsChild,
            points,
            badgeCount,
            isClasslinkUser,
        } = props;
        const buttonValue = isModifyingChild
            ? strings.deleteName(targetNickname)
            : t`Delete your account`;
        const fromOwnSettingsPage =
            location.search.indexOf("ref=settings") > -1;
        return (
            <RequireReauth
                identifier={actorIdentifier}
                showClasslinkAuthProvider={isClasslinkUser}
                onSuccess={() => setReauthSuccessful(true)}
                onFailure={() => {}}
            >
                {(invokeReauth) => (
                    <React.Fragment>
                        <ModalLauncher
                            opened={reauthSuccessful}
                            onClose={() => setReauthSuccessful(false)}
                            modal={({closeModal}) => (
                                <DeleteAccountModal
                                    isModifyingChild={isModifyingChild}
                                    fromOwnSettingsPage={fromOwnSettingsPage}
                                    badgeCount={badgeCount}
                                    points={points}
                                    isChild={targetIsChild}
                                    nickname={targetNickname}
                                    kaid={targetId}
                                    onClose={closeModal}
                                />
                            )}
                        />
                        <Button
                            onClick={invokeReauth}
                            style={styles.button}
                            actionType="destructive"
                        >
                            {buttonValue}
                        </Button>
                    </React.Fragment>
                )}
            </RequireReauth>
        );
    }

    function renderMaybeDeleteAccount(): React.ReactElement {
        const {actorIsChild, targetHasChildren} = props;
        if (actorIsChild) {
            return (
                <Body>
                    {t`Your parent must log in to delete your account.`}
                </Body>
            );
        }
        if (targetHasChildren) {
            return (
                <Body>
                    {t`You must delete all of your child accounts before deleting your own account.`}
                </Body>
            );
        }
        return renderDeleteButton();
    }

    return (
        <View role="region" aria-label={t`Delete account settings`}>
            {/**
             * Note: The id on the following element is important; it
             * allows the mobile app to link directly to this heading
             * so our users can delete their account from the app.
             * This is a requirement from Apple to remain in the
             * AppStore. Do not alter this id without first consulting
             * with Khan/mobile.
             */}
            <HeadingSmall style={styles.header} tag="h2" id={id}>
                {t`Delete account`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            {renderMaybeDeleteAccount()}
        </View>
    );
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
});

type ExportProps = JSX.LibraryManagedAttributes<
    typeof DeleteAccount,
    React.ComponentProps<typeof DeleteAccount>
>;

export default DeleteAccount as React.ComponentType<ExportProps>;
